/**
 * Native Visitor Tracker — Phase 1 MVP
 *
 * Goal: capture real-visitor behaviour (pageviews · clicks on tracked CTAs ·
 * scroll depth · time-on-site heartbeats · exit pages) and ship it to the
 * backend `POST /api/visitors/event` ingest endpoint.
 *
 * Privacy:
 *   - No PII captured. We deliberately DO NOT record typed prompt content,
 *     passwords, payment fields, auth tokens, email addresses, or any
 *     element value. Only the `data-track` element identifier + the visible
 *     text label (trimmed to 140 chars) make it onto the wire.
 *   - Logged-out visitors get an anonymous `anon_*` id stored in
 *     localStorage (long-lived) + a per-session `vs_*` id in
 *     sessionStorage. Logged-in users are stamped server-side from the
 *     existing session cookie — the tracker never reads the cookie.
 *
 * Exclusion:
 *   - The frontend opts itself out via the `is_persona_sim` payload flag
 *     when running inside the Effectiveness Lab persona simulator.
 *   - The server-side classifier also catches bots / admins / Emergent
 *     preview testing. The tracker is "fail open" — even if a few admin
 *     events sneak through, they get marked `is_excluded=true` and are
 *     filtered out of all admin reports.
 *
 * Reliability:
 *   - Events are buffered for up to 5s OR 20 events before flushing.
 *   - On page hide / unload we drain via `navigator.sendBeacon` so the
 *     exit event always lands.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const INGEST_URL = `${API_BASE}/api/visitors/event`;
const HEARTBEAT_MS = 15_000;
const FLUSH_MS = 5_000;
const FLUSH_MAX = 20;
const SCROLL_MILESTONES = [25, 50, 75, 100];

// ─── Session id helpers ─────────────────────────────────────────────────────
function _rand(prefix) {
  const buf = new Uint8Array(12);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  return `${prefix}_${Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function _getOrCreateAnonId() {
  try {
    let id = localStorage.getItem("vt_anon_id");
    if (!id) {
      id = _rand("anon");
      localStorage.setItem("vt_anon_id", id);
    }
    return id;
  } catch (_e) {
    return _rand("anon");
  }
}

function _getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem("vt_session_id");
    if (!id) {
      id = _rand("vs");
      sessionStorage.setItem("vt_session_id", id);
    }
    return id;
  } catch (_e) {
    return _rand("vs");
  }
}

// ─── Core tracker state ─────────────────────────────────────────────────────
let _started = false;
let _queue = [];
let _flushTimer = null;
let _heartbeatTimer = null;
let _accumActiveMs = 0;
let _scrollMaxPct = 0;
let _scrollMilestonesFired = new Set();
let _activeAccumStart = null;
let _currentPath = null;
let _sessionId = null;
let _anonId = null;
let _isPersonaSim = false;

// ─── Page classification (which page is this visitor on?) ──────────────────
function _currentPagePath() {
  try {
    // Strip query/hash so analytics doesn't fragment on UTM params.
    const u = new URL(window.location.href);
    return u.pathname || "/";
  } catch (_e) {
    return window.location.pathname || "/";
  }
}

// ─── Queue + flush ──────────────────────────────────────────────────────────
function _enqueue(ev) {
  _queue.push({ ts: new Date().toISOString(), page: _currentPath, ...ev });
  if (_queue.length >= FLUSH_MAX) _flushNow();
  else if (!_flushTimer) _flushTimer = setTimeout(_flushNow, FLUSH_MS);
}

function _payload(events) {
  return {
    session_id: _sessionId,
    anon_id: _anonId,
    events,
    user_agent: navigator.userAgent || "",
    referrer: document.referrer || "",
    is_persona_sim: _isPersonaSim,
  };
}

async function _flushNow() {
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
  if (!_queue.length) return;
  const batch = _queue.splice(0, _queue.length);
  try {
    await fetch(INGEST_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(_payload(batch)),
      keepalive: true,
    });
  } catch (_e) {
    // Drop on error. Next flush will carry forward newer events; we
    // deliberately don't retry to keep the ingestion idempotent-ish and
    // avoid retry storms when the user is offline.
  }
}

function _beaconFlush(extraEvents = []) {
  // Used on page hide/unload — synchronous-ish via sendBeacon. Includes
  // any tail events plus whatever's in the queue.
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
  const batch = _queue.splice(0, _queue.length).concat(extraEvents);
  if (!batch.length) return;
  try {
    const blob = new Blob([JSON.stringify(_payload(batch))], { type: "application/json" });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(INGEST_URL, blob);
    } else {
      fetch(INGEST_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(_payload(batch)),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_e) { /* best-effort */ }
}

// ─── Heartbeat: time-on-site (only while tab is visible) ───────────────────
function _startActiveAccumulator() {
  if (_activeAccumStart === null) _activeAccumStart = Date.now();
}
function _stopActiveAccumulator() {
  if (_activeAccumStart !== null) {
    _accumActiveMs += Date.now() - _activeAccumStart;
    _activeAccumStart = null;
  }
}
function _emitHeartbeat() {
  // Accumulator gets flushed into a heartbeat event with the elapsed ms.
  _stopActiveAccumulator();
  if (_accumActiveMs > 0) {
    _enqueue({ type: "heartbeat", duration_ms: _accumActiveMs });
    _accumActiveMs = 0;
  }
  // Restart only if still visible
  if (document.visibilityState === "visible") _startActiveAccumulator();
}

// ─── Scroll: emit milestones (25 / 50 / 75 / 100) ──────────────────────────
function _onScroll() {
  try {
    const h = document.documentElement;
    const scrolled = window.scrollY + window.innerHeight;
    const total = h.scrollHeight;
    if (!total || total <= window.innerHeight) return;
    const pct = Math.max(0, Math.min(100, Math.round((scrolled / total) * 100)));
    if (pct > _scrollMaxPct) _scrollMaxPct = pct;
    for (const m of SCROLL_MILESTONES) {
      if (pct >= m && !_scrollMilestonesFired.has(m)) {
        _scrollMilestonesFired.add(m);
        _enqueue({ type: "scroll", scroll_pct: m });
      }
    }
  } catch (_e) { /* noop */ }
}

// ─── Click delegation: capture `data-track` elements anywhere on the page ──
function _onClick(e) {
  // Walk up to find the nearest element with data-track
  let el = e.target;
  for (let hops = 0; el && hops < 6; hops++, el = el.parentElement) {
    const track = el && el.getAttribute && el.getAttribute("data-track");
    if (track) {
      const label = (el.innerText || el.getAttribute("aria-label") || "").trim().slice(0, 140);
      _enqueue({
        type: "click",
        element: track,
        element_text: label || null,
      });
      return;
    }
  }
}

// ─── Pageview wiring ────────────────────────────────────────────────────────
function _emitPageviewIfChanged() {
  const path = _currentPagePath();
  if (path !== _currentPath) {
    _currentPath = path;
    // Reset scroll milestones — each route gets its own measurement.
    _scrollMaxPct = 0;
    _scrollMilestonesFired = new Set();
    _enqueue({ type: "pageview" });
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────
export function startVisitorTracker(opts = {}) {
  if (_started || typeof window === "undefined") return;
  _started = true;
  _isPersonaSim = !!opts.isPersonaSim;
  _sessionId = _getOrCreateSessionId();
  _anonId = _getOrCreateAnonId();

  // Honor Do-Not-Track: skip tracker entirely (still keeps the IDs minted).
  if (navigator.doNotTrack === "1" || window.doNotTrack === "1") {
    return;
  }

  _emitPageviewIfChanged();
  _startActiveAccumulator();

  // Detect SPA navigations (React Router uses history.pushState).
  const _origPush = window.history.pushState;
  const _origReplace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const r = _origPush.apply(this, args);
    setTimeout(_emitPageviewIfChanged, 0);
    return r;
  };
  window.history.replaceState = function (...args) {
    const r = _origReplace.apply(this, args);
    setTimeout(_emitPageviewIfChanged, 0);
    return r;
  };
  window.addEventListener("popstate", _emitPageviewIfChanged);
  document.addEventListener("click", _onClick, true);
  window.addEventListener("scroll", _onScroll, { passive: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      _startActiveAccumulator();
    } else {
      _stopActiveAccumulator();
      _flushNow();
    }
  });

  // Heartbeat ticker.
  _heartbeatTimer = setInterval(_emitHeartbeat, HEARTBEAT_MS);

  // Exit hook: drain everything via sendBeacon.
  window.addEventListener("pagehide", () => {
    _stopActiveAccumulator();
    const tail = [];
    if (_accumActiveMs > 0) {
      tail.push({
        ts: new Date().toISOString(),
        page: _currentPath,
        type: "heartbeat",
        duration_ms: _accumActiveMs,
      });
      _accumActiveMs = 0;
    }
    tail.push({
      ts: new Date().toISOString(),
      page: _currentPath,
      type: "exit",
    });
    _beaconFlush(tail);
  });
}

// Optional explicit hook — for places the SPA navigation listener can't see
// (e.g. third-party iframe transitions). Most code should NOT need this.
export function trackEvent(type, fields = {}) {
  if (!_started) return;
  _enqueue({ type, ...fields });
}
